"use strict";

import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  ListItemText,
  MenuItem,
  Select,
  Slider,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import VoicePreviewRow from "./VoicePreviewRow.js";
import { ADD_REMIXED_VOICE_TO_CHARACTER } from "../../ApiEndpoints.js";

const SELECT_ALL_VALUE = "__select_all__";

const VOICE_DESCRIPTION_MIN_LENGTH = 5;
const VOICE_DESCRIPTION_MAX_LENGTH = 1000;
const TEXT_MIN_LENGTH = 100;
const TEXT_MAX_LENGTH = 1000;

const EMOTION_OPTIONS = ["neutral", "happy", "sad", "angry", "afraid"];

const PROMPT_STRENGTH_PRESETS = {
  low: 0.25,
  medium: 0.5,
  high: 0.75,
};

const getCharacterKey = (character, index) => {
  return (
    character.speaker ||
    character.id ||
    character.characterId ||
    character.name ||
    `character-${index}`
  );
};

const getCharacterLabel = (character) => {
  return (
    character.display_name ||
    character.displayName ||
    character.speaker ||
    "Unnamed Character"
  );
};

const normalizeGenerationResponse = (responseData) => {
  if (Array.isArray(responseData)) return responseData;

  if (Array.isArray(responseData?.results)) {
    return responseData.results;
  }

  if (responseData?.previews) {
    return [responseData];
  }

  return [];
};

const VoiceCreationSection = ({
  title,
  endpoint,
  projectId,
  projectName,
  characters,
  allowMultipleCharacters,
  defaultVoiceDescription = "Make this voice have a Mexican Accent",
}) => {
  const [selectedCharacterKeys, setSelectedCharacterKeys] = useState([]);
  const [selectedEmotion, setSelectedEmotion] = useState("neutral");

  const [voiceDescription, setVoiceDescription] = useState(
    defaultVoiceDescription
  );

  const [text, setText] = useState("");
  const [autoGenerateText, setAutoGenerateText] = useState(false);
  const [loudness, setLoudness] = useState(0.5);
  const [seed] = useState("");
  const [guidanceScale, setGuidanceScale] = useState(2);
  const [streamPreviews] = useState(false);
  const [remixingSessionId] = useState("");
  const [remixingSessionIterationId] = useState("");
  const [promptStrengthPreset, setPromptStrengthPreset] = useState("medium");

  const [voiceResults, setVoiceResults] = useState([]);
  const [acceptedVoiceIds, setAcceptedVoiceIds] = useState([]);
  const [acceptingVoiceIds, setAcceptingVoiceIds] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const sortedCharacters = useMemo(() => {
    return [...characters].sort((a, b) => {
      const nameA = getCharacterLabel(a);
      const nameB = getCharacterLabel(b);
      return nameA.localeCompare(nameB);
    });
  }, [characters]);

  const selectableCharacterKeys = useMemo(() => {
    return sortedCharacters.map((character, index) =>
      getCharacterKey(character, index)
    );
  }, [sortedCharacters]);

  useEffect(() => {
    setSelectedCharacterKeys((previous) =>
      previous.filter((key) => selectableCharacterKeys.includes(key))
    );
  }, [selectableCharacterKeys]);

  const selectedCharacters = useMemo(() => {
    return sortedCharacters.filter((character, index) =>
      selectedCharacterKeys.includes(getCharacterKey(character, index))
    );
  }, [sortedCharacters, selectedCharacterKeys]);

  const allSelected =
    selectableCharacterKeys.length > 0 &&
    selectedCharacterKeys.length === selectableCharacterKeys.length;

  const someSelected =
    selectedCharacterKeys.length > 0 &&
    selectedCharacterKeys.length < selectableCharacterKeys.length;

  const getCharacterBySpeakerId = (speakerId) => {
    return sortedCharacters.find((character) => character.speaker === speakerId);
  };

  const handleCharacterSelectionChange = (event) => {
    if (!allowMultipleCharacters) {
      const nextValue = event.target.value;
      setSelectedCharacterKeys(nextValue ? [nextValue] : []);
      return;
    }

    const value = event.target.value;
    const nextValue = typeof value === "string" ? value.split(",") : value;

    if (nextValue.includes(SELECT_ALL_VALUE)) {
      setSelectedCharacterKeys(allSelected ? [] : selectableCharacterKeys);
      return;
    }

    setSelectedCharacterKeys(nextValue);
  };

  const validatePayload = () => {
    const cleanVoiceDescription = voiceDescription.trim();

    if (selectedCharacters.length === 0) {
      toast.error(
        allowMultipleCharacters
          ? "Please select at least one character."
          : "Please select one character."
      );
      return false;
    }

    if (
      cleanVoiceDescription.length < VOICE_DESCRIPTION_MIN_LENGTH ||
      cleanVoiceDescription.length > VOICE_DESCRIPTION_MAX_LENGTH
    ) {
      toast.error("Voice description must be between 5 and 1000 characters.");
      return false;
    }

    const cleanText = text.trim();

    if (
      !autoGenerateText &&
      cleanText &&
      (cleanText.length < TEXT_MIN_LENGTH || cleanText.length > TEXT_MAX_LENGTH)
    ) {
      toast.error("Text must be between 100 and 1000 characters when provided.");
      return false;
    }

    if (seed !== "") {
      const seedNumber = Number(seed);

      if (
        !Number.isInteger(seedNumber) ||
        seedNumber < 0 ||
        seedNumber > 2147483647
      ) {
        toast.error("Seed must be an integer between 0 and 2147483647.");
        return false;
      }
    }

    return true;
  };

  const handleAcceptPreview = async ({
    generatedVoiceId,
    speakerId,
    emotion,
  }) => {
    if (!generatedVoiceId) {
      toast.error("Missing generated voice id.");
      return;
    }

    if (!speakerId) {
      toast.error("Missing speaker id.");
      return;
    }

    try {
      setAcceptingVoiceIds((previous) => {
        if (previous.includes(generatedVoiceId)) return previous;
        return [...previous, generatedVoiceId];
      });

      await axios.post(ADD_REMIXED_VOICE_TO_CHARACTER, {
        project_id: projectId,
        speaker_id: speakerId,
        emotion,
        voice_id: generatedVoiceId,
      });

      setAcceptedVoiceIds((previous) => {
        if (previous.includes(generatedVoiceId)) return previous;
        return [...previous, generatedVoiceId];
      });

      toast.success("Voice added successfully.");
    } catch (error) {
      console.error("Failed to add voice:", error);
      toast.error("Failed to add voice.");
    } finally {
      setAcceptingVoiceIds((previous) =>
        previous.filter((id) => id !== generatedVoiceId)
      );
    }
  };

  const handleGenerateVoices = async () => {
    if (!validatePayload()) return;

    try {
      setIsSubmitting(true);
      setVoiceResults([]);
      setAcceptedVoiceIds([]);
      setAcceptingVoiceIds([]);

      const payload = {
        project_id: projectId,
        project_name: projectName,

        voice_description: voiceDescription.trim(),
        text: autoGenerateText ? null : text.trim() || null,
        auto_generate_text: autoGenerateText,
        loudness: Number(loudness),
        seed: seed === "" ? null : Number(seed),
        guidance_scale: Number(guidanceScale),
        stream_previews: streamPreviews,
        remixing_session_id: remixingSessionId.trim() || null,
        remixing_session_iteration_id:
          remixingSessionIterationId.trim() || null,
        prompt_strength: PROMPT_STRENGTH_PRESETS[promptStrengthPreset],

        character_voices: selectedCharacters.map((character) => ({
          speaker_id: character.speaker || "",
          emotion: selectedEmotion,
        })),
      };

      const response = await axios.post(endpoint, payload);

      const normalizedResults = normalizeGenerationResponse(response.data).map(
        (result, index) => ({
          ...result,
          parent_speaker_id:
            result.parent_speaker_id ||
            selectedCharacters[index]?.speaker ||
            selectedCharacters[0]?.speaker ||
            "",
          selected_emotion: selectedEmotion,
        })
      );

      setVoiceResults(normalizedResults);

      if (streamPreviews) {
        toast.info(
          "stream_previews is enabled, so the backend may return generated IDs without audio_base_64."
        );
      }

      if (normalizedResults.length === 0) {
        toast.warning(`${title} completed, but no previews were returned.`);
        return;
      }

      toast.success(`${title} previews generated successfully.`);
    } catch (error) {
      console.error(`Failed to run ${title}:`, error);
      toast.error(`Failed to create ${title.toLowerCase()} previews.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit =
    selectedCharacters.length > 0 &&
    voiceDescription.trim().length >= VOICE_DESCRIPTION_MIN_LENGTH &&
    !isSubmitting;

  const characterSelectValue = allowMultipleCharacters
    ? selectedCharacterKeys
    : selectedCharacterKeys[0] || "";

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        border: "1px solid rgba(223, 230, 238, 0.95)",
        borderRadius: "26px",
        backgroundColor: "rgba(255,255,255,0.96)",
        boxShadow: "0 18px 45px rgba(15, 23, 42, 0.08)",
        overflow: "hidden",
        backdropFilter: "blur(8px)",
      }}
    >
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: 1.6,
          borderBottom: "1px solid #edf0f5",
          background:
            "linear-gradient(135deg, rgba(31,111,150,0.08), rgba(255,255,255,0.96))",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 850,
            letterSpacing: "-0.02em",
            lineHeight: 1.2,
          }}
        >
          {title}
        </Typography>
      </Box>

      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: { xs: 2, md: 3 },
          py: 1.75,
          borderBottom: "1px solid #edf0f5",
          backgroundColor: "#ffffff",
        }}
        spacing={2}
      >
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <Select
              multiple={allowMultipleCharacters}
              value={characterSelectValue}
              displayEmpty
              onChange={handleCharacterSelectionChange}
              renderValue={(selected) => {
                if (allowMultipleCharacters) {
                  if (!selected || selected.length === 0) {
                    return "Select characters";
                  }

                  if (selected.length === selectableCharacterKeys.length) {
                    return `All characters (${selected.length})`;
                  }

                  return (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((characterKey) => {
                        const character = sortedCharacters.find(
                          (item, index) =>
                            getCharacterKey(item, index) === characterKey
                        );

                        return (
                          <Chip
                            key={characterKey}
                            size="small"
                            label={
                              character
                                ? getCharacterLabel(character)
                                : characterKey
                            }
                          />
                        );
                      })}
                    </Box>
                  );
                }

                if (!selected) return "Select character";

                const character = sortedCharacters.find(
                  (item, index) => getCharacterKey(item, index) === selected
                );

                return character ? getCharacterLabel(character) : selected;
              }}
              sx={{
                borderRadius: "12px",
                minHeight: 44,
                backgroundColor: "#f9fafb",
                fontWeight: 650,
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                },
              }}
            >
              {allowMultipleCharacters && (
                <MenuItem value={SELECT_ALL_VALUE}>
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                  />
                  <ListItemText primary="Select all" />
                </MenuItem>
              )}

              {sortedCharacters.length === 0 && (
                <MenuItem value="" disabled>
                  No characters found
                </MenuItem>
              )}

              {sortedCharacters.map((character, index) => {
                const characterKey = getCharacterKey(character, index);

                return (
                  <MenuItem key={characterKey} value={characterKey}>
                    {allowMultipleCharacters && (
                      <Checkbox
                        checked={selectedCharacterKeys.includes(characterKey)}
                      />
                    )}

                    <ListItemText
                      primary={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                          }}
                        >
                          <span>{getCharacterLabel(character)}</span>

                          {!!character.projectId && (
                            <span
                              title="Imported Character"
                              style={{
                                color: "#FFD700",
                                fontSize: "var(--font-size-normal)",
                              }}
                            >
                              ↙️
                            </span>
                          )}
                        </Box>
                      }
                    />
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={3}>
          <FormControl fullWidth size="small">
            <Select
              value={selectedEmotion}
              onChange={(event) => setSelectedEmotion(event.target.value)}
              sx={{
                borderRadius: "12px",
                minHeight: 44,
                backgroundColor: "#f9fafb",
                fontWeight: 650,
              }}
            >
              {EMOTION_OPTIONS.map((emotion) => (
                <MenuItem key={emotion} value={emotion}>
                  {emotion.charAt(0).toUpperCase() + emotion.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={2}>
          <FormControl fullWidth size="small">
            <Select
              value={promptStrengthPreset}
              onChange={(event) =>
                setPromptStrengthPreset(event.target.value)
              }
              sx={{
                borderRadius: "12px",
                minHeight: 44,
                backgroundColor: "#f9fafb",
                fontWeight: 650,
              }}
            >
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: 2.5,
          borderBottom: "1px solid #edf0f5",
          backgroundColor: "#ffffff",
        }}
      >
        <TextField
          fullWidth
          multiline
          minRows={4}
          maxRows={8}
          value={voiceDescription}
          onChange={(event) => setVoiceDescription(event.target.value)}
          placeholder="Make this voice have a Mexican Accent"
          inputProps={{ maxLength: VOICE_DESCRIPTION_MAX_LENGTH }}
          variant="outlined"
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "16px",
              backgroundColor: "#fbfcfe",
              alignItems: "flex-start",
            },
            "& textarea": {
              fontSize: 17,
              lineHeight: 1.55,
              color: "#111827",
            },
            "& textarea::placeholder": {
              color: "#777c89",
              opacity: 1,
            },
          }}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mt: 0.75,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color:
                voiceDescription.length >= VOICE_DESCRIPTION_MAX_LENGTH
                  ? "error.main"
                  : "#8a94a6",
              fontWeight: 650,
            }}
          >
            {voiceDescription.length} / {VOICE_DESCRIPTION_MAX_LENGTH}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          mx: { xs: 2, md: 3 },
          my: 2.5,
          p: { xs: 2, md: 2.5 },
          border: "1px solid #edf0f5",
          borderRadius: "20px",
          backgroundColor: "#fbfcfe",
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 2 }}>
          Voice generation controls
        </Typography>

        <Grid container spacing={2.25}>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={autoGenerateText}
                  onChange={(event) =>
                    setAutoGenerateText(event.target.checked)
                  }
                />
              }
              label="Auto-generate preview text"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Preview text"
              value={text}
              onChange={(event) => setText(event.target.value)}
              disabled={autoGenerateText}
              inputProps={{ maxLength: TEXT_MAX_LENGTH }}
              helperText={
                autoGenerateText
                  ? "Text will be generated automatically."
                  : `${text.length} / ${TEXT_MAX_LENGTH}. Optional, but must be 100-1000 characters when provided.`
              }
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "14px",
                  backgroundColor: autoGenerateText ? "#f3f4f6" : "#ffffff",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Loudness: {loudness}
            </Typography>
            <Slider
              value={loudness}
              min={-1}
              max={1}
              step={0.1}
              onChange={(event, value) => setLoudness(Number(value))}
              sx={{
                color: "#1f6f96",
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Guidance Scale: {guidanceScale}
            </Typography>
            <Slider
              value={guidanceScale}
              min={0}
              max={100}
              step={1}
              onChange={(event, value) => setGuidanceScale(Number(value))}
              sx={{
                color: "#1f6f96",
              }}
            />
          </Grid>
{/* 
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Seed"
              type="number"
              value={seed}
              onChange={(event) => setSeed(event.target.value)}
              inputProps={{
                min: 0,
                max: 2147483647,
                step: 1,
              }}
              helperText="Optional"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "14px",
                  backgroundColor: "#ffffff",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Remixing Session ID"
              value={remixingSessionId}
              onChange={(event) => setRemixingSessionId(event.target.value)}
              helperText="Optional"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "14px",
                  backgroundColor: "#ffffff",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Session Iteration ID"
              value={remixingSessionIterationId}
              onChange={(event) =>
                setRemixingSessionIterationId(event.target.value)
              }
              helperText="Optional"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "14px",
                  backgroundColor: "#ffffff",
                },
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={streamPreviews}
                  onChange={(event) => setStreamPreviews(event.target.checked)}
                />
              }
              label="Stream previews instead of returning audio_base_64"
            />

            <Typography
              variant="caption"
              sx={{ display: "block", color: "#6b7280" }}
            >
              Keep this off when you want the audio preview rows to be playable
              directly from the API response.
            </Typography>
          </Grid> */}
        </Grid>
      </Box>

      {voiceResults.length > 0 && (
        <Box
          sx={{
            mx: { xs: 2, md: 3 },
            mb: 2,
            border: "1px solid #e5e7eb",
            borderRadius: "18px",
            overflow: "hidden",
            backgroundColor: "white",
          }}
        >
          {voiceResults.map((result, resultIndex) => {
            const resultSpeakerId =
              result.parent_speaker_id ||
              selectedCharacters[resultIndex]?.speaker ||
              selectedCharacters[0]?.speaker ||
              "";

            const resultCharacter = getCharacterBySpeakerId(resultSpeakerId);

            const resultCharacterName = resultCharacter
              ? getCharacterLabel(resultCharacter)
              : resultSpeakerId || `Character ${resultIndex + 1}`;

            const resultEmotion = result.selected_emotion || selectedEmotion;

            const previews = Array.isArray(result.previews)
              ? result.previews
              : [];

            return (
              <Box
                key={`${resultSpeakerId || "voice"}-${resultIndex}`}
                sx={{
                  borderTop:
                    resultIndex === 0 ? "none" : "1px solid #d1d5db",
                }}
              >
                <Box
                  sx={{
                    px: 2,
                    py: 1.25,
                    backgroundColor: "#fafbfc",
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {resultCharacterName}
                  </Typography>

                  {!!result.text && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#6b7280",
                        lineHeight: 1.5,
                      }}
                    >
                      {result.text}
                    </Typography>
                  )}
                </Box>

                {previews.map((preview, previewIndex) => (
                  <VoicePreviewRow
                    key={`${
                      preview.generated_voice_id ||
                      `${resultSpeakerId}-${previewIndex}`
                    }`}
                    preview={preview}
                    characterName={resultCharacterName}
                    index={previewIndex}
                    speakerId={resultSpeakerId}
                    emotion={resultEmotion}
                    isAccepted={acceptedVoiceIds.includes(
                      preview.generated_voice_id
                    )}
                    isAccepting={acceptingVoiceIds.includes(
                      preview.generated_voice_id
                    )}
                    onAccept={handleAcceptPreview}
                  />
                ))}
              </Box>
            );
          })}
        </Box>
      )}

      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: 2,
          borderTop: "1px solid #edf0f5",
          display: "flex",
          justifyContent: "flex-end",
          backgroundColor: "#ffffff",
        }}
      >
        <IconButton
          onClick={handleGenerateVoices}
          disabled={!canSubmit}
          sx={{
            width: 50,
            height: 50,
            borderRadius: "50%",
            color: "white",
            background:
              canSubmit
                ? "linear-gradient(135deg, #1f6f96, #2f8fb7)"
                : "#cfd6df",
            boxShadow: canSubmit
              ? "0 10px 22px rgba(31, 111, 150, 0.28)"
              : "none",
            transition: "all 160ms ease",
            "&:hover": {
              background:
                canSubmit
                  ? "linear-gradient(135deg, #195b7c, #277fa5)"
                  : "#cfd6df",
              transform: canSubmit ? "translateY(-1px)" : "none",
            },
            "&.Mui-disabled": {
              color: "white",
              background: "#cfd6df",
            },
          }}
        >
          <KeyboardArrowUpIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

export default VoiceCreationSection;